import type { AgencyGraphJson } from '@saaga/shared/interfaces/agency.interface'
import type { AgentEdge, AgentNode } from '@saaga/shared/interfaces/agency-node.interface'
import { DEFAULT_AGENT_NODE_DATA } from '@saaga/shared/interfaces/agency-node.interface'
import { downloadJsonFile } from '@saaga/shared/utils/download-file.util'
import {
  type AgencyFormSchemaType,
  agencyCategoryList,
} from '@/components/agencies/agency-form.schema'

export interface AgencyExportJson {
  slug: string
  name: string
  shortDescription?: string
  longDescription?: string
  bestFor?: string
  systemPrompt?: string
  categoryList?: string[]
  canvas: AgencyGraphJson
}

interface AgencyImportResult {
  formFields: Partial<AgencyFormSchemaType>
  canvas?: AgencyGraphJson
}

const MAX_IMPORT_FILE_SIZE_BYTES = 5 * 1024 * 1024

const IMPORTABLE_STRING_FIELD_LIST = [
  'slug',
  'name',
  'shortDescription',
  'longDescription',
  'bestFor',
  'systemPrompt',
] as const

export const buildAgencyExportJson = (
  formValues: AgencyFormSchemaType,
  flowData: AgencyGraphJson
): AgencyExportJson => ({
  slug: formValues.slug,
  name: formValues.name,
  shortDescription: formValues.shortDescription,
  longDescription: formValues.longDescription,
  bestFor: formValues.bestFor,
  systemPrompt: formValues.systemPrompt,
  categoryList: formValues.categoryList,
  canvas: flowData,
})

export const buildExportFileName = (slug?: string): string =>
  slug ? `${slug}.json` : 'agency-export.json'

export const downloadAgencyAsJson = (exportData: AgencyExportJson, slug?: string): void => {
  downloadJsonFile(exportData, buildExportFileName(slug))
}

const extractStringFields = (raw: Record<string, unknown>): Partial<AgencyFormSchemaType> => {
  const result: Partial<AgencyFormSchemaType> = {}
  for (const field of IMPORTABLE_STRING_FIELD_LIST) {
    if (typeof raw[field] === 'string') {
      result[field] = raw[field]
    }
  }
  return result
}

const VALID_CATEGORY_SET = new Set<string>(agencyCategoryList)

const extractCategoryList = (raw: Record<string, unknown>): Partial<AgencyFormSchemaType> => {
  if (!Array.isArray(raw.categoryList)) return {}
  const validList = raw.categoryList
    .filter((c): c is string => typeof c === 'string')
    .filter((c) => VALID_CATEGORY_SET.has(c)) as AgencyFormSchemaType['categoryList']
  if (!validList || validList.length === 0) return {}
  return { categoryList: validList }
}

const isValidNodePosition = (pos: unknown): pos is { x: number; y: number } => {
  if (typeof pos !== 'object' || pos === null) return false
  const p = pos as Record<string, unknown>
  return typeof p.x === 'number' && typeof p.y === 'number'
}

const pickStringField = <K extends string>(
  raw: Record<string, unknown>,
  key: K
): Record<K, string> | Record<string, never> =>
  typeof raw[key] === 'string' ? ({ [key]: raw[key] } as Record<K, string>) : {}

const pickNodeDataFields = (raw: Record<string, unknown>): Partial<AgentNode['data']> => ({
  ...pickStringField(raw, 'label'),
  ...pickStringField(raw, 'roleDescription'),
  ...pickStringField(raw, 'systemPrompt'),
  ...pickStringField(raw, 'model'),
  ...pickStringField(raw, 'imageUrl'),
  ...(Array.isArray(raw.toolList)
    ? { toolList: raw.toolList.filter((t): t is string => typeof t === 'string') }
    : {}),
  ...(typeof raw.temperature === 'number' ? { temperature: raw.temperature } : {}),
  ...(typeof raw.isEntryPoint === 'boolean' ? { isEntryPoint: raw.isEntryPoint } : {}),
})

const sanitizeNodeData = (data: unknown): AgentNode['data'] => {
  if (typeof data !== 'object' || data === null) return { ...DEFAULT_AGENT_NODE_DATA }
  return { ...DEFAULT_AGENT_NODE_DATA, ...pickNodeDataFields(data as Record<string, unknown>) }
}

const extractCanvas = (raw: Record<string, unknown>): AgencyGraphJson | undefined => {
  if (typeof raw.canvas !== 'object' || raw.canvas === null) return undefined
  const canvas = raw.canvas as Record<string, unknown>
  if (!Array.isArray(canvas.nodeList)) return undefined

  const nodeList = canvas.nodeList
    .filter((n): n is Record<string, unknown> => typeof n === 'object' && n !== null)
    .filter((n) => typeof n.id === 'string' && isValidNodePosition(n.position))
    .map(
      (n): AgentNode => ({
        id: n.id as string,
        type: 'agent',
        position: n.position as { x: number; y: number },
        data: sanitizeNodeData(n.data),
      })
    )

  const nodeIdSet = new Set(nodeList.map((n) => n.id))

  const edgeList = Array.isArray(canvas.edgeList)
    ? canvas.edgeList
        .filter((e): e is Record<string, unknown> => typeof e === 'object' && e !== null)
        .filter(
          (e) =>
            typeof e.id === 'string' && typeof e.source === 'string' && typeof e.target === 'string'
        )
        .filter((e) => nodeIdSet.has(e.source as string) && nodeIdSet.has(e.target as string))
        .map(
          (e): AgentEdge => ({
            id: e.id as string,
            source: e.source as string,
            target: e.target as string,
            ...(typeof e.label === 'string' ? { label: e.label } : {}),
            ...(typeof e.type === 'string' ? { type: e.type } : {}),
            ...(typeof e.animated === 'boolean' ? { animated: e.animated } : {}),
          })
        )
    : []

  return { nodeList, edgeList }
}

export const parseAgencyImportJson = (raw: Record<string, unknown>): AgencyImportResult => ({
  formFields: {
    ...extractStringFields(raw),
    ...extractCategoryList(raw),
  },
  canvas: extractCanvas(raw),
})

export const readAgencyFromFile = (file: File): Promise<AgencyImportResult> =>
  new Promise((resolve, reject) => {
    if (file.size > MAX_IMPORT_FILE_SIZE_BYTES) {
      reject(new Error('File is too large. Maximum allowed size is 5MB.'))
      return
    }

    const reader = new FileReader()

    reader.onload = () => {
      try {
        const raw: unknown = JSON.parse(reader.result as string)
        if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
          reject(new Error('Invalid file: expected a JSON object'))
          return
        }
        resolve(parseAgencyImportJson(raw as Record<string, unknown>))
      } catch (error) {
        if (error instanceof SyntaxError) {
          reject(new Error('Invalid JSON file'))
          return
        }
        reject(error)
      }
    }

    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsText(file)
  })
